import {
  Cancel,
  CropFreeOutlined,
  Download,
  OpenInFull,
  PlayArrow,
  Remove,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import React, { useEffect, useState, useMemo } from "react";
import {
  convertDate,
  convertDateOnly,
  convertUTCDateToLocalTime,
} from "src/app/utils/helper";
import dateFormat from "src/app/utils/dateFormat";
import DateRangePickerModal from "src/features/trends/modals/DatePickerModal";
import {
  getDownloadAlertsInstructions,
  getDownloadStatusLog,
} from "src/app/services";
import { enqueueSnackbar } from "notistack";
import SimulateAlertModal from "src/features/common/alertsAndInstructions/SimulateAlertModal";
import { USER_NAME_ROLE } from "src/app/auth";

export interface CardWidgetProps {
  headerLabel: string;
  showDate?: string;
  headerIcon?: React.ReactElement;
  content: React.ReactNode;
  subHeadingRight?: React.ReactNode;
  footerContent?: React.ReactNode;
  initiallyCollapsed?: boolean;
  setIsLatestReportOpen?: Function;
  setIsLiveStatusOpen?: Function;
  fullScreenContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  section?: "top" | "middle" | "bottom";
  moduleId?: any;
  moduleType?: string;
  formData?: any;
  onAlertRefresh?: () => void;
}

const CardWidget = ({
  headerLabel,
  showDate,
  headerContent,
  headerIcon,
  content,
  subHeadingRight,
  footerContent,
  setIsLatestReportOpen,
  setIsLiveStatusOpen,
  initiallyCollapsed,
  fullScreenContent,
  section,
  moduleId,
  moduleType,
  formData,
  onAlertRefresh,
}: CardWidgetProps) => {
  const [collapsed, setCollapsed] = useState(initiallyCollapsed || false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openSimulateAlert, setOpenSimulateAlert] = useState<boolean>(false);

  const [isDownloading, setIsdownloading] = useState(false);
  const [toggleDatePicker, setToggleDatePicker] = useState(false);
  const [dateRangeValues, setDateRangeValues] = useState<any>({
    endDate: new Date(),
    startDate: new Date(),
    key: "downloadDateRange",
  });

  const isAdmin = sessionStorage.getItem(USER_NAME_ROLE) === "admin";

  const isBelow1800Pixel = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(1800)
  );

  useEffect(() => {
    if (setIsLatestReportOpen) {
      setIsLatestReportOpen(!collapsed || openModal);
    }
    if (setIsLiveStatusOpen) {
      setIsLiveStatusOpen(!collapsed || openModal);
    }
  }, [collapsed, openModal]);

  const getSectionSx = useMemo(() => {
    const topSection =
      section === "top" && !collapsed
        ? {
          p: { md: 2, lg: "0px", xl: 2 },
          pt: headerContent ? 0 : 2,
          borderRadius: 3,
          boxShadow: "2px 4px 8px #00000029",
          transition: "all .2s linear",
          minHeight: { xl: "34vh", lg: "40vh", md: "40vh" },
          maxHeight: { xl: "34vh", lg: "40vh", md: "40vh" },
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "0.4em",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,.2)",
            borderRadius: "0.2em",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(0,0,0,.3)",
          },
        }
        : {
          p: 2,
          pt: headerContent ? 0 : 2,
          borderRadius: 3,
          boxShadow: "2px 4px 8px #00000029",
          transition: "all .2s linear",
        };
    return section === "middle" && !collapsed
      ? {
        p: 2,
        pt: headerContent ? 0 : 2,
        borderRadius: 3,
        boxShadow: "2px 4px 8px #00000029",
        transition: "all .2s linear",
        minHeight: "30vh",
      }
      : topSection;
  }, [section, collapsed]);

  const calculatedButtonDate = useMemo(() => {
    if (dateRangeValues?.startDate && dateRangeValues?.endDate) {
      return `${dateFormat(dateRangeValues?.startDate)}-${dateFormat(
        dateRangeValues?.endDate
      )}`;
    }
    return "StartDate-EndDate";
  }, [dateRangeValues]);

  const downloadStatusLog = (dateRange: any) => {
    if (dateRange.startDate && dateRange.endDate) {
      const payload = {
        startDate: convertDate(dateRange.startDate),
        endDate: convertDate(dateRange.endDate),
        moduleId,
      };

      setIsdownloading(true);

      // Decide which API to use
      const isAlerts = headerLabel === "Alerts & Instructions";
      const apiCall = isAlerts ? getDownloadAlertsInstructions : getDownloadStatusLog;

      apiCall(payload)
        .then((res) => {
          const parsedFormData = JSON.parse(formData);

          // -----------------------------
          // FILE NAME BUILDER
          // -----------------------------
          const baseName = `${parsedFormData?.asset_name} - ${parsedFormData?.equipment_name}`;
          const datePart = `${convertDateOnly(dateRange.startDate)}-${convertDateOnly(dateRange.endDate)}`;
          const typeSuffix = isAlerts ? "alerts" : "status-log";
          const extension = isAlerts ? "csv" : "log";

          const fileName = `${baseName}-${datePart}-${typeSuffix}.${extension}`;

          // -----------------------------
          // TRIGGER FILE DOWNLOAD
          // -----------------------------
          const url = window.URL.createObjectURL(res.data);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();

          // reset state
          setDateRangeValues({
            endDate: new Date(),
            startDate: new Date(),
          });

          setIsdownloading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsdownloading(false);
          enqueueSnackbar({
            message:
              error?.response?.data?.Message ||
              error?.Message ||
              "Something went wrong!",
            variant: "error",
          });
        });
    }
  };

  return (
    <Paper sx={getSectionSx}>
      <Box component="div" sx={{ display: "flex", alignItems: "center" }}>
        {!headerContent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {headerIcon && (
                <Box
                  sx={{
                    mr: 1,
                    color: "#4d4e4e",
                    height: "20px",
                    width: "18px",
                  }}
                >
                  {headerIcon}
                </Box>
              )}

              <Typography
                variant="body1"
                component={"span"}
                noWrap
                title={headerLabel}
                sx={{
                  fontSize: "12px",
                  color: "#4d4e4e",
                  fontWeight: 600,
                  letterSpacing: "0.08px",
                  maxWidth: "350px",
                  cursor: "pointer",
                  // "&:hover": { overflow: 'visible',
                  // whiteSpace: 'normal' }
                }}
              >
                {headerLabel}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "500",
                  fontSize: "9px",
                  mr: "20px",
                }}
              >
                {showDate
                  ? ` Updated on ${convertUTCDateToLocalTime(showDate)}`
                  : ""}
              </Typography>
              {isBelow1800Pixel && (
                <Box>
                  {subHeadingRight && (
                    <Box sx={{ mr: isBelow1800Pixel ? 0 : 1, ml: 1 }}>
                      {subHeadingRight}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
        {headerContent != undefined && headerContent}
        <Box
          component="section"
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            color: "#4D4E4E",
            // color: (theme) => theme.palette.color38.main,
          }}
        >
          {!isBelow1800Pixel && subHeadingRight && (
            <Box sx={{ mr: 1 }}>{subHeadingRight}</Box>
          )}
          {fullScreenContent && (
            <>
              {(moduleType === "Torque") && (
                <Box component="section" sx={{ mr: "4px", cursor: "pointer" }}>
                  <Download
                    sx={{ fontSize: "16px" }}
                    onClick={() => setToggleDatePicker(true)}
                  />
                </Box>
              )}
              {isAdmin && headerLabel === "Alerts & Instructions" && (
                <Tooltip title="Simulate Alert">
                  <Box component="section" sx={{ mr: "4px", cursor: "pointer" }}>
                    <PlayArrow
                      sx={{ fontSize: "16px" }}
                      onClick={() => setOpenSimulateAlert(true)}
                    />
                  </Box>
                </Tooltip>
              )}
              <Box component="section" sx={{ mr: "4px", cursor: "pointer" }}>
                <OpenInFull
                  sx={{ fontSize: "14px" }}
                  onClick={() => setOpenModal(true)}
                />
              </Box>
            </>
          )}

          <Box>
            {collapsed ? (
              <>
                <CropFreeOutlined
                  onClick={() => setCollapsed((prev) => !prev)}
                  sx={{ fontSize: "14px", cursor: "pointer" }}
                />
              </>
            ) : (
              <>
                <Remove
                  onClick={() => setCollapsed((prev) => !prev)}
                  sx={{ fontSize: "14px", cursor: "pointer" }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
      {!collapsed && (
        <Box
          sx={{ transition: "all 0.3s ease-out", opacity: collapsed ? 0 : 1 }}
        >
          <Divider
            sx={{
              mt: headerContent ? 0 : 1,
              mb: 1,
              height: "0px",
              border: "1px solid #E8E8ED",
            }}
          />
          <Box>{content}</Box>
          {footerContent && (
            <Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 1,
                  height: "0px",
                  border: "1px solid #E8E8ED",
                }}
              />
              <Box>{footerContent}</Box>
            </Box>
          )}
        </Box>
      )}
      {openModal && fullScreenContent && (
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth={"xl"}
        >
          <DialogContent sx={{ minWidth: "80vw" }}>
            <Box component="div" sx={{ display: "flex", alignItems: "center" }}>
              <Box component="section">
                <Box sx={{ display: "flex" }}>
                  {headerIcon && (
                    <Box
                      sx={{
                        mr: 1,
                        color: "#4d4e4e",
                        height: "20px",
                        width: "18px",
                      }}
                    >
                      {headerIcon}
                    </Box>
                  )}

                  <Typography
                    variant="body1"
                    component={"span"}
                    sx={{
                      fontSize: "20px",
                      color: "#4d4e4e",
                      fontWeight: 600,
                      letterSpacing: "0.08px",
                    }}
                  >
                    {headerLabel}
                  </Typography>
                </Box>

                {isBelow1800Pixel && (
                  <Box>
                    {subHeadingRight && (
                      <Box sx={{ mr: isBelow1800Pixel ? 0 : 1, ml: 1 }}>
                        {subHeadingRight}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Box
                component="section"
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  color: "#4D4E4E",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: "12px",
                    mr: "20px",
                  }}
                >
                  {showDate
                    ? ` Updated on ${convertUTCDateToLocalTime(showDate)}`
                    : ""}
                </Typography>
                {!isBelow1800Pixel && subHeadingRight && (
                  <Box sx={{ mr: 1 }}>{subHeadingRight}</Box>
                )}
                <Box>
                  <>
                    <Cancel
                      onClick={() => setOpenModal(false)}
                      sx={{ fontSize: "16px", cursor: "pointer" }}
                    />
                  </>
                </Box>
              </Box>
            </Box>
            <Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 1,
                  height: "0px",
                  border: "1px solid #E8E8ED",
                }}
              />
              <Box>{fullScreenContent}</Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
      <DateRangePickerModal
        open={toggleDatePicker}
        onClose={() => setToggleDatePicker(false)}
        onApply={downloadStatusLog}
        dateRangeValues={dateRangeValues}
        setDateRangeValues={setDateRangeValues}
      />
      <SimulateAlertModal
        open={openSimulateAlert}
        onClose={() => setOpenSimulateAlert(false)}
        moduleId={moduleId}
        moduleType={moduleType}
        moduleName={formData ? (() => {
          try {
            const parsed = JSON.parse(formData);
            return `${parsed?.asset_name || ""} - ${parsed?.equipment_name || "Module"}`;
          } catch {
            return "Module";
          }
        })() : "Module"}
        onSuccess={() => {
          // Refresh alerts data after successful simulation
          if (onAlertRefresh) {
            onAlertRefresh();
          }
        }}
      />
    </Paper>
  );
};
export default CardWidget;
